import React, { useState, useEffect } from 'react';
import { PackagePlus, Server, QrCode, Hash, Save, Factory, Layers, Monitor } from 'lucide-react';
import { toast } from 'react-toastify';
import { addDeviceStock, getAllDeviceTypes, getAllManufacturers, getAllModels } from '../services/api';

const AddStock = () => {
    const [formData, setFormData] = useState({
        deviceTypeId: '',
        manufacturerId: '',
        modelNoId: '',
        macAddress: '',
        serialNo: ''
    });
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    
    // Master Data
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [models, setModels] = useState([]);

    useEffect(() => {
        const fetchMasters = async () => {
            try {
                const [typesRes, manuRes, modelsRes] = await Promise.all([
                    getAllDeviceTypes(1, 100),
                    getAllManufacturers(1, 100),
                    getAllModels(1, 100)
                ]);

                if (typesRes?.success) setDeviceTypes(typesRes.data);
                if (manuRes?.success) setManufacturers(manuRes.data);
                if (modelsRes?.success) setModels(modelsRes.data);
            } catch (error) {
                console.error("Failed to fetch master data:", error);
                toast.error("Failed to load dropdown options");
            } finally {
                setDataLoading(false);
            }
        };
        fetchMasters();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updates = { [name]: value };
            // Reset model if manufacturer changes
            if (name === 'manufacturerId') {
                updates.modelNoId = '';
            }
            return { ...prev, ...updates };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const payload = {
                deviceTypeId: Number(formData.deviceTypeId),
                manufacturerId: Number(formData.manufacturerId),
                modelNoId: Number(formData.modelNoId),
                macAddress: formData.macAddress,
                serialNo: formData.serialNo
            };

            const response = await addDeviceStock(payload);
            
            if (response.success) {
                toast.success("Stock added successfully!");
                setFormData({
                    deviceTypeId: '',
                    manufacturerId: '',
                    modelNoId: '',
                    macAddress: '',
                    serialNo: ''
                });
            } else {
                toast.error(response.message || "Failed to add stock.");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to add stock.");
        } finally {
            setLoading(false);
        }
    };

    // Filter models based on selected manufacturer
    const filteredModels = models.filter(m => m.manufacturerId === Number(formData.manufacturerId));

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                    <PackagePlus className="h-6 w-6 text-blue-600" />
                    Add Stock
                </h1>
                <p className="text-sm text-gray-500 mt-1">Register new hardware inventory into the system.</p>
            </div>

            {/* Form Card */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-semibold text-gray-900">Device Information</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Please provide the hardware details below.</p>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {dataLoading ? (
                        <div className="space-y-4 animate-pulse">
                            <div className="h-10 bg-gray-100 rounded w-full"></div>
                            <div className="h-10 bg-gray-100 rounded w-full"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Device Type */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                                    <Monitor className="h-3.5 w-3.5" />
                                    Device Type
                                </label>
                                <select
                                    name="deviceTypeId"
                                    value={formData.deviceTypeId}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none"
                                >
                                    <option value="" disabled>Select a device type...</option>
                                    {deviceTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.type}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Manufacturer */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                                    <Factory className="h-3.5 w-3.5" />
                                    Manufacturer
                                </label>
                                <select
                                    name="manufacturerId"
                                    value={formData.manufacturerId}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none"
                                >
                                    <option value="" disabled>Select manufacturer...</option>
                                    {manufacturers.map(m => (
                                        <option key={m.id} value={m.id}>{m.company}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Model (Dependent) */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                                    <Layers className="h-3.5 w-3.5" />
                                    Model Number
                                </label>
                                <select
                                    name="modelNoId"
                                    value={formData.modelNoId}
                                    onChange={handleChange}
                                    required
                                    disabled={!formData.manufacturerId}
                                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                    <option value="" disabled>
                                        {formData.manufacturerId ? "Select model..." : "Select manufacturer first..."}
                                    </option>
                                    {filteredModels.map(m => (
                                        <option key={m.id} value={m.id}>{m.modelNo}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Serial Number */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                                    <Hash className="h-3.5 w-3.5" />
                                    Serial Number
                                </label>
                                <input
                                    type="text"
                                    name="serialNo"
                                    value={formData.serialNo}
                                    onChange={handleChange}
                                    required
                                    placeholder="e.g. SN-2024-XXXX"
                                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-mono"
                                />
                            </div>

                            {/* MAC Address */}
                            <div className="space-y-1.5 md:col-span-2">
                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                                    <QrCode className="h-3.5 w-3.5" />
                                    MAC Address
                                </label>
                                <input
                                    type="text"
                                    name="macAddress"
                                    value={formData.macAddress}
                                    onChange={handleChange}
                                    required
                                    placeholder="AA:BB:CC:DD:EE:FF"
                                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-mono"
                                />
                            </div>
                        </div>
                    )}

                    <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100 mt-6">
                        <button
                            type="button"
                            onClick={() => setFormData({ deviceTypeId: '', manufacturerId: '', modelNoId: '', macAddress: '', serialNo: '' })}
                            className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-all"
                            disabled={loading || dataLoading}
                        >
                            Reset
                        </button>
                        <button
                            type="submit"
                            disabled={loading || dataLoading}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 shadow-sm active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    <span>Save Stock</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddStock;
